/**
 * Auto-hydration scanner for the POLST widget (POL-587).
 *
 * When the bundle loads in a browser, this module scans the host DOM
 * for marker elements carrying any of:
 *
 *   - `data-polst="<shortId>"`          → render a polst card
 *   - `data-polst-campaign="<id>"`      → render a campaign player
 *   - `data-polst-brand="<slug>"`       → render a brand feed
 *
 * …and hydrates each one with the matching mode renderer.
 *
 * It also implements the "script-tag self-hydration" pattern
 * (Disqus / Intercom / Substack): if the loading `<script>` element
 * itself carries one of the markers, we either mount into the
 * element pointed to by `data-target="#selector"` or insert a fresh
 * `<div>` immediately before the script tag and mount into that.
 *
 * Every hydrated container is stamped with `data-polst-hydrated="1"`
 * so repeated script loads / scans are idempotent.
 *
 * Optionally, if `<html>` or `<body>` carries `data-polst-auto-observe`,
 * a single `MutationObserver` is installed to hydrate marker elements
 * that are inserted into the DOM after the initial scan
 * (SPA navigation, modal open, etc.).
 *
 * Contract:
 *   - The module is side-effect safe in SSR: the bootstrap guard
 *     (`typeof document === "undefined"`) short-circuits every
 *     browser-only code path.
 *   - All logged errors use the `[polst]` prefix, matching the
 *     widget-wide house convention.
 *   - The scanner never throws into the host page. Renderer errors
 *     are caught and logged.
 *   - `mountAutoTarget()` is the internal entry point POL-588's
 *     `window.Polst.mount` will re-export — keep its shape stable.
 */

import {
  getBrandCampaigns,
  getBrandFeed,
  getBrandPolsts,
  getCampaign,
  getPolst,
} from "./api";
import { getSharedEmitter, type AnalyticsEmitter } from "./analytics";
import {
  renderBrandFeed,
  type BrandFeedHandle,
  type BrandFeedMode,
  type BrandFeedOptions,
} from "./brand-feed";
import {
  renderCampaignPlayer,
  type CampaignPlayerDTO,
  type CampaignPlayerHandle,
  type CampaignPlayerOptions,
} from "./campaign-player";
import {
  renderPolstCard,
  type PolstCardDTO,
  type PolstCardOptions,
  type PolstHandle,
} from "./polst-card";
import { createRoot, type ShadowRootBundle } from "./shadow-root";
import { normalizeThemeMode, type ThemeMode } from "./theme";

// ─── Public types ───────────────────────────────────────────────────────────

export type MountAutoKind = "polst" | "campaign" | "brand";

/**
 * Raw config parsed off a marker element's (or script tag's) `data-*`
 * attributes. Callback names are resolved against `window`; unknown
 * names are logged and stripped.
 */
export interface AutoHydrateConfig {
  kind: MountAutoKind;
  id: string;
  /**
   * POL-788: per-call API origin override. When set, the render path's
   * REST fetches go to this origin instead of the widget global
   * (`configureOrigins`) or the build-time default. Per-call only — does
   * NOT mutate the global. Lets SDK consumers point a single render at
   * staging without process-wide config.
   */
  apiOrigin?: string;
  theme?: ThemeMode;
  accent?: string;
  size?: string;
  hideTitle?: boolean;
  hideBrand?: boolean;
  autoAdvance?: boolean;
  mode?: BrandFeedMode;
  onVote?: (event: unknown) => void;
  onComplete?: (event: unknown) => void;
}

export interface MountAutoTargetOptions extends AutoHydrateConfig {
  /** Override the Document used for element creation (testing). */
  document?: Document;
  /**
   * When true, skip network fetches and pass through injected data.
   * Used by tests; production auto-hydration never sets this.
   */
  fetchPolstOverride?: (id: string) => Promise<PolstCardDTO>;
  fetchCampaignOverride?: (id: string) => Promise<CampaignPlayerDTO>;
  /**
   * Override the analytics emitter (tests). Defaults to the shared
   * module-scope emitter built lazily by `./analytics`.
   */
  analyticsEmitter?: AnalyticsEmitter;
}

export interface MountAutoHandle {
  kind: MountAutoKind;
  /** The polst-card / campaign-player / brand-feed handle returned by the mode renderer. */
  renderer: PolstHandle | CampaignPlayerHandle | BrandFeedHandle | null;
  shadow: ShadowRootBundle;
  /** Detach the mode renderer and dispose the Shadow DOM. */
  destroy(): void;
}

// ─── Constants ──────────────────────────────────────────────────────────────

const HYDRATED_ATTR = "data-polst-hydrated";
const OBSERVE_ATTR = "data-polst-auto-observe";
const MARKER_SELECTOR =
  "[data-polst]:not(script), [data-polst-campaign]:not(script), [data-polst-brand]:not(script)";
const LOG_PREFIX = "[polst]";

/**
 * `document.currentScript` is only truthy while *that* script is
 * executing — once the async listeners fire, `document.currentScript`
 * is null. Capture it at module-eval time so later code can still
 * inspect the loading tag.
 */
const CURRENT_SCRIPT: HTMLScriptElement | null =
  typeof document !== "undefined" && document.currentScript instanceof HTMLScriptElement
    ? (document.currentScript as HTMLScriptElement)
    : null;

let bootstrapInstalled = false;
let observerInstalled = false;
let activeObserver: MutationObserver | null = null;

// ─── Idempotency + predicates ──────────────────────────────────────────────

export function isHydrated(el: Element): boolean {
  return el.getAttribute(HYDRATED_ATTR) === "1";
}

function markHydrated(el: Element): void {
  el.setAttribute(HYDRATED_ATTR, "1");
}

// ─── Config parsing ─────────────────────────────────────────────────────────

const TRUE_VALUES = new Set(["", "true", "1", "yes", "on"]);
const FALSE_VALUES = new Set(["false", "0", "no", "off"]);

/**
 * Parse a `data-*` boolean attribute. Empty string (attribute present
 * without value) is truthy — matches the HTML `disabled`/`hidden`
 * convention that web authors will expect.
 */
function parseBool(value: string | null): boolean | undefined {
  if (value === null) return undefined;
  const lower = value.trim().toLowerCase();
  if (TRUE_VALUES.has(lower)) return true;
  if (FALSE_VALUES.has(lower)) return false;
  // Unknown value: treat as presence = true. Bad data rarely means
  // "please disable the feature you just opted into".
  return true;
}

function parseTheme(value: string | null): ThemeMode | undefined {
  if (value === null) return undefined;
  const normalized = value.trim().toLowerCase();
  if (normalized === "") return undefined;
  // Defer to the theme module's canonical normalizer so we inherit the
  // same fallback semantics (invalid value → "auto") used everywhere
  // else in the widget.
  return normalizeThemeMode(normalized as ThemeMode);
}

function parseMode(value: string | null): BrandFeedMode | undefined {
  if (!value) return undefined;
  const normalized = value.trim().toLowerCase();
  if (normalized === "polsts" || normalized === "campaigns" || normalized === "mixed") {
    return normalized;
  }
  return undefined;
}

/**
 * Resolve a callback name against `window`. Returns undefined (and
 * logs a warning) when the name does not point at a function.
 * Side-effect: all warnings use the shared `[polst]` prefix.
 */
function resolveCallback(
  name: string | null,
  attrName: string,
): ((event: unknown) => void) | undefined {
  if (!name) return undefined;
  const trimmed = name.trim();
  if (trimmed === "") return undefined;
  if (typeof window === "undefined") return undefined;
  const candidate = (window as unknown as Record<string, unknown>)[trimmed];
  if (typeof candidate !== "function") {
    console.warn(
      `${LOG_PREFIX} ${attrName} callback '${trimmed}' is not a function on window; skipping`,
    );
    return undefined;
  }
  return candidate as (event: unknown) => void;
}

/**
 * Determine which marker this element carries. Returns null when the
 * element is not a marker (or only carries the hydrated attr).
 */
function detectKind(el: Element): { kind: MountAutoKind; id: string } | null {
  const polst = el.getAttribute("data-polst");
  if (polst !== null && polst !== "") {
    return { kind: "polst", id: polst };
  }
  const campaign = el.getAttribute("data-polst-campaign");
  if (campaign !== null && campaign !== "") {
    return { kind: "campaign", id: campaign };
  }
  const brand = el.getAttribute("data-polst-brand");
  if (brand !== null && brand !== "") {
    return { kind: "brand", id: brand };
  }
  return null;
}

/**
 * Read the full auto-hydrate config off an element. Returns null if
 * the element is not a marker.
 */
export function readConfig(el: Element): AutoHydrateConfig | null {
  const detected = detectKind(el);
  if (!detected) return null;

  const config: AutoHydrateConfig = { ...detected };

  const theme = parseTheme(el.getAttribute("data-theme"));
  if (theme) config.theme = theme;

  const accent = el.getAttribute("data-accent");
  if (accent && accent.trim() !== "") config.accent = accent.trim();

  const size = el.getAttribute("data-size");
  if (size && size.trim() !== "") config.size = size.trim();

  const hideTitle = parseBool(el.getAttribute("data-hide-title"));
  if (hideTitle !== undefined) config.hideTitle = hideTitle;

  const hideBrand = parseBool(el.getAttribute("data-hide-brand"));
  if (hideBrand !== undefined) config.hideBrand = hideBrand;

  const autoAdvance = parseBool(el.getAttribute("data-auto-advance"));
  if (autoAdvance !== undefined) config.autoAdvance = autoAdvance;

  const mode = parseMode(el.getAttribute("data-mode"));
  if (mode) config.mode = mode;

  const onVote = resolveCallback(el.getAttribute("data-on-vote"), "data-on-vote");
  if (onVote) config.onVote = onVote;

  const onComplete = resolveCallback(el.getAttribute("data-on-complete"), "data-on-complete");
  if (onComplete) config.onComplete = onComplete;

  return config;
}

// ─── Mode dispatch ──────────────────────────────────────────────────────────

/**
 * Internal mount entry point. Creates a Shadow DOM root on the
 * container, fetches any data the renderer needs, then calls the
 * matching mode renderer. Exported so POL-588's `window.Polst.mount`
 * can re-export it.
 *
 * `container` must be an Element that is already attached to the
 * document (otherwise the mode renderers have no Document to allocate
 * elements against).
 */
export async function mountAutoTarget(
  container: Element,
  options: MountAutoTargetOptions,
): Promise<MountAutoHandle> {
  const shadow = createRoot(container, { theme: options.theme });

  const hostDoc =
    options.document ??
    ((container as unknown as { ownerDocument?: Document }).ownerDocument as Document | undefined);

  // Analytics emitter — shared across the whole bundle unless the
  // caller injects an override (tests). `getSharedEmitter()` is a
  // lazy getter, so SSR imports of `./auto-hydrate` stay side-effect
  // free.
  const emitter = options.analyticsEmitter ?? getSharedEmitter();

  if (options.kind === "brand") {
    const brandOptions: BrandFeedOptions = {};
    if (options.mode) brandOptions.mode = options.mode;
    if (hostDoc) brandOptions.document = hostDoc;
    // POL-788: when an apiOrigin override is set, route brand-feed's
    // default fetchers through it so the gallery hits the same env as
    // the polst/campaign render paths. The fetcher overrides take a
    // (brandSlug, cursor, limit) tuple; we ignore cursor/limit here
    // because the underlying api.ts getters don't yet accept them
    // (matches the existing wrapDefault* behaviour in brand-feed.ts).
    if (options.apiOrigin) {
      const apiOpts = { baseUrl: options.apiOrigin };
      // BrandListResponse<T> is `{ data, nextCursor }` (matches wire),
      // and brand-feed.ts's BrandFeedPage<T> is also `{ data, nextCursor }`,
      // so the wrappers are passthroughs (no field rename).
      brandOptions.fetchPolsts = (slug) =>
        getBrandPolsts(slug, apiOpts).then((r) => ({
          data: r.data as unknown as never[],
          nextCursor: r.nextCursor ?? null,
        }));
      brandOptions.fetchCampaigns = (slug) =>
        getBrandCampaigns(slug, apiOpts).then((r) => ({
          data: r.data as unknown as never[],
          nextCursor: r.nextCursor ?? null,
        }));
      brandOptions.fetchFeed = (slug) =>
        getBrandFeed(slug, apiOpts).then((r) => ({
          data: r.data as unknown as never[],
          nextCursor: r.nextCursor ?? null,
        }));
    }
    const handle = renderBrandFeed(shadow.root, options.id, brandOptions);
    emitter.emit({ type: "embed.viewed", brandId: options.id });
    return {
      kind: "brand",
      renderer: handle,
      shadow,
      destroy(): void {
        handle.destroy();
        shadow.dispose();
      },
    };
  }

  // POL-788: when an apiOrigin override is set, route the api.ts
  // getter through it. Otherwise call the getter unchanged so the
  // module-scope default client (honoring `configureOrigins(...)` /
  // build-time default) handles it. The `if` keeps the call signature
  // identical to pre-POL-788 in the no-override path so call-site
  // tests asserting `toHaveBeenCalledWith("id")` still pass.
  if (options.kind === "polst") {
    const fetchPolst =
      options.fetchPolstOverride ??
      (async (id: string) => {
        const summary = options.apiOrigin
          ? await getPolst(id, { baseUrl: options.apiOrigin })
          : await getPolst(id);
        return summary as unknown as PolstCardDTO;
      });
    const dto = await fetchPolst(options.id);
    const polstOptions: PolstCardOptions = {};
    if (options.hideBrand !== undefined) polstOptions.hideBrand = options.hideBrand;
    const userOnVote = options.onVote;
    const brandId = dto.brand?.slug;
    polstOptions.onVote = (event: { polstId: string; option: "A" | "B" }) => {
      // Fire analytics first so a throwing host callback still gets
      // the event onto the wire.
      const voteEvent: {
        type: "embed.voted";
        polstId: string;
        brandId?: string;
      } = { type: "embed.voted", polstId: event.polstId };
      if (brandId) voteEvent.brandId = brandId;
      emitter.emit(voteEvent);
      if (userOnVote) {
        try {
          (userOnVote as (e: unknown) => void)(event);
        } catch (err) {
          console.error(`${LOG_PREFIX} onVote host callback threw:`, err);
        }
      }
    };
    if (hostDoc) polstOptions.document = hostDoc;
    const handle = renderPolstCard(shadow.root, dto, polstOptions);
    const viewedEvent: {
      type: "embed.viewed";
      polstId: string;
      brandId?: string;
    } = { type: "embed.viewed", polstId: options.id };
    if (brandId) viewedEvent.brandId = brandId;
    emitter.emit(viewedEvent);
    return {
      kind: "polst",
      renderer: handle,
      shadow,
      destroy(): void {
        handle.destroy();
        shadow.dispose();
      },
    };
  }

  // campaign
  const fetchCampaign =
    options.fetchCampaignOverride ??
    (async (id: string) => {
      const summary = options.apiOrigin
        ? await getCampaign(id, { baseUrl: options.apiOrigin })
        : await getCampaign(id);
      return summary as unknown as CampaignPlayerDTO;
    });
  const dto = await fetchCampaign(options.id);
  const campaignOptions: CampaignPlayerOptions = {};
  if (options.autoAdvance !== undefined) campaignOptions.autoAdvance = options.autoAdvance;
  const userOnVote = options.onVote;
  const campaignBrandId = dto.brand?.slug;
  campaignOptions.onStepVote = (event: {
    campaignId: string;
    polstId: string;
    option: "A" | "B";
    stepIndex: number;
    totalSteps: number;
    completedSteps: number;
  }) => {
    const evt: {
      type: "embed.voted";
      campaignId: string;
      polstId: string;
      brandId?: string;
    } = {
      type: "embed.voted",
      campaignId: event.campaignId,
      polstId: event.polstId,
    };
    if (campaignBrandId) evt.brandId = campaignBrandId;
    emitter.emit(evt);
    if (userOnVote) {
      try {
        (userOnVote as (e: unknown) => void)(event);
      } catch (err) {
        console.error(`${LOG_PREFIX} onVote host callback threw:`, err);
      }
    }
  };
  const userOnComplete = options.onComplete;
  campaignOptions.onComplete = (event: {
    campaignId: string;
    totalSteps: number;
    completedAt: string;
    choices: Array<{ polstId: string; option: "A" | "B" | null }>;
  }) => {
    const evt: {
      type: "embed.completed";
      campaignId: string;
      brandId?: string;
    } = {
      type: "embed.completed",
      campaignId: event.campaignId,
    };
    if (campaignBrandId) evt.brandId = campaignBrandId;
    emitter.emit(evt);
    if (userOnComplete) {
      try {
        (userOnComplete as (e: unknown) => void)(event);
      } catch (err) {
        console.error(`${LOG_PREFIX} onComplete host callback threw:`, err);
      }
    }
  };
  if (hostDoc) campaignOptions.document = hostDoc;
  const handle = renderCampaignPlayer(shadow.root, dto, campaignOptions);
  const viewedEvent: {
    type: "embed.viewed";
    campaignId: string;
    brandId?: string;
  } = { type: "embed.viewed", campaignId: options.id };
  if (campaignBrandId) viewedEvent.brandId = campaignBrandId;
  emitter.emit(viewedEvent);
  return {
    kind: "campaign",
    renderer: handle,
    shadow,
    destroy(): void {
      handle.destroy();
      shadow.dispose();
    },
  };
}

// ─── Script-tag self-hydration ──────────────────────────────────────────────

/**
 * For script-tag self-hydration, resolve the Element to mount into.
 * Returns null when `data-target` is set but does not resolve — the
 * caller then logs an error and skips the hydration.
 *
 * If `data-target` is absent, a sibling `<div>` is created and
 * inserted immediately before the `<script>` tag.
 */
function resolveScriptTarget(
  script: HTMLScriptElement,
  doc: Document,
): Element | null {
  const target = script.getAttribute("data-target");
  if (target && target.trim() !== "") {
    const el = doc.querySelector(target.trim());
    if (!el) {
      console.error(
        `${LOG_PREFIX} script-tag data-target '${target}' did not resolve; skipping hydration`,
      );
      return null;
    }
    return el;
  }

  // Insert a fresh container just before the script. The container
  // inherits the script tag's context (flow position, CSS ancestors)
  // so the widget sits exactly where the author dropped the tag.
  const parent = script.parentNode;
  if (!parent) {
    console.error(
      `${LOG_PREFIX} script-tag has no parentNode; cannot insert container`,
    );
    return null;
  }
  const host = doc.createElement("div");
  host.setAttribute("data-polst-host", "");
  parent.insertBefore(host, script);
  return host;
}

// ─── Hydration ──────────────────────────────────────────────────────────────

/**
 * Hydrate a single marker element. `sourceEl` is the element whose
 * `data-*` attributes drive the config (either the marker itself or
 * the loading script tag). `container` is the element the widget
 * renders into.
 *
 * Errors are caught and logged — we never re-throw into the host.
 */
async function hydrateContainer(sourceEl: Element, container: Element): Promise<void> {
  if (isHydrated(container)) return;
  const config = readConfig(sourceEl);
  if (!config) return;

  // Mark before await so concurrent MutationObserver ticks and a second
  // `autoHydrate()` pass do not both race to hydrate the same node.
  markHydrated(container);

  try {
    await mountAutoTarget(container, config);
  } catch (err) {
    console.error(`${LOG_PREFIX} failed to hydrate ${config.kind} '${config.id}':`, err);
  }
}

/**
 * Hydrate every marker element currently in the document + the
 * loading script tag (if it carries markers). Safe to call multiple
 * times — already-hydrated containers are skipped by the idempotency
 * marker.
 */
export function autoHydrate(): void {
  if (typeof document === "undefined") return;

  try {
    // 1. Script-tag self-hydration. Runs first so the author's
    //    "drop one tag and render inline" pattern wins the earliest
    //    layout slot.
    if (CURRENT_SCRIPT && detectKind(CURRENT_SCRIPT)) {
      const container = resolveScriptTarget(CURRENT_SCRIPT, document);
      if (container) {
        // Don't mount into a container that already hosts a widget.
        if (!isHydrated(container)) {
          void hydrateContainer(CURRENT_SCRIPT, container);
        }
      }
    }

    // 2. Marker elements.
    const markers = document.querySelectorAll(MARKER_SELECTOR);
    markers.forEach((el) => {
      if (isHydrated(el)) return;
      void hydrateContainer(el, el);
    });
  } catch (err) {
    // Defensive: querySelectorAll / forEach throwing in a host's weird
    // Document polyfill should not break the widget.
    console.error(`${LOG_PREFIX} autoHydrate() failed:`, err);
  }
}

// ─── MutationObserver ───────────────────────────────────────────────────────

function hasObserveFlag(doc: Document): boolean {
  const html = doc.documentElement;
  const body = doc.body;
  return (
    (html !== null && html.hasAttribute(OBSERVE_ATTR)) ||
    (body !== null && body.hasAttribute(OBSERVE_ATTR))
  );
}

/**
 * Install a single `MutationObserver` watching the document for
 * marker elements added after the initial scan. Gated by the
 * `data-polst-auto-observe` flag on either `<html>` or `<body>` so
 * hosts that don't need dynamic hydration don't pay for the watch.
 */
export function installMutationObserver(): void {
  if (typeof document === "undefined" || typeof MutationObserver === "undefined") return;
  if (observerInstalled) return;
  if (!hasObserveFlag(document)) return;

  const observer = new MutationObserver((mutations) => {
    for (const mut of mutations) {
      mut.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;
        // The added node itself might be a marker.
        if (detectKind(node) && !isHydrated(node)) {
          void hydrateContainer(node, node);
        }
        // Or descendants might be. querySelectorAll is safe on
        // disconnected subtrees once the node is attached, which it
        // is by the time MutationObserver fires.
        const descendants = node.querySelectorAll(MARKER_SELECTOR);
        descendants.forEach((el) => {
          if (!isHydrated(el)) void hydrateContainer(el, el);
        });
      });
    }
  });
  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
  activeObserver = observer;
  observerInstalled = true;
}

// ─── Bootstrap ──────────────────────────────────────────────────────────────

/**
 * Install the `DOMContentLoaded` listener (or run immediately if the
 * document is already past interactive). Idempotent: repeated imports
 * of the widget bundle only attach once.
 */
export function installAutoHydrateBootstrap(): void {
  if (typeof document === "undefined") return;
  if (bootstrapInstalled) return;
  bootstrapInstalled = true;

  const run = (): void => {
    autoHydrate();
    installMutationObserver();
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", run, { once: true });
  } else {
    // Document is already interactive or complete — the script was
    // loaded async / deferred / dropped after the initial parse.
    run();
  }
}

// Side effect: auto-install on import. `installAutoHydrateBootstrap`
// is idempotent and SSR-safe, so importing `@polst-web/widget` from a
// Node build step still works.
installAutoHydrateBootstrap();

// ─── Test-only reset ────────────────────────────────────────────────────────

/**
 * Reset internal state. Test-only — the `__` prefix keeps it off the
 * surface area that script / SDK consumers see.
 */
export function __resetAutoHydrate(): void {
  bootstrapInstalled = false;
  observerInstalled = false;
  if (activeObserver) {
    activeObserver.disconnect();
    activeObserver = null;
  }
}
